import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  BookmarkCheck,
  CalendarPlus,
  ChevronDown,
  ChevronRight,
  Loader2,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { GetEventsMonthly } from "@/services/api/user/getCalendarEvents";
import type { StudentEvent } from "@/services/api/user/events";
import {
  hasEventEnded,
  isMultiDayRun,
  localized,
  parseEventDate,
  pickLocalizedName,
  postTypeLabelKey,
  toDateKey,
  toImageList,
  toUniversityCodes,
  withMomentLocale,
  POST_TYPE_BADGE_CLASS,
  POST_TYPE_LABELS,
} from "@/utils/eventFormat";
import { downloadEventIcs } from "@/utils/eventCalendar";
import { universityList } from "@/assets/universityList";
import StudentMonthGrid from "@/components/student/StudentMonthGrid";
import StudentSubscribeButton from "@/components/student/StudentSubscribeButton";
import AdminEmptyState from "@/components/AdminEmptyState";

/** Safety cap on how many days one event may be spread across. */
const MAX_RANGE_DAYS = 2000;

const ALL = "all";

type CalendarFilters = {
  postType: string;
  university: string;
  society: string;
  subscribedOnly: boolean;
  multiDayOnly: boolean;
};

const EMPTY_FILTERS: CalendarFilters = {
  postType: ALL,
  university: ALL,
  society: ALL,
  subscribedOnly: false,
  multiDayOnly: false,
};

const FILTER_SELECT_CLASS =
  "w-full appearance-none rounded-xl border border-gray-200 bg-white px-3 py-2 pr-8 text-sm text-gray-700 outline-none transition focus:border-[#ffa217] focus:ring-2 focus:ring-[#ffa217]/30";

/**
 * Inclusive `YYYY-MM-DD` bounds of an event's run, or null when it has no
 * usable start date. A missing end date (or one that precedes the start) means
 * a single-day event, so the range collapses onto the start date.
 */
const eventRangeKeys = (event: StudentEvent): [string, string] | null => {
  const startKey = toDateKey(event.details.start_date);
  if (!startKey) return null;
  const endKey = toDateKey(event.details.end_date);
  if (!endKey || endKey < startKey) return [startKey, startKey];
  return [startKey, endKey];
};

/**
 * Match the month grid's day-cell order: subscribed events first, then the
 * earliest start time, with an event that has no start time (an all-day or
 * period run) sorted to the top of its group.
 */
const compareBySubscribedThenTime = (
  a: StudentEvent,
  b: StudentEvent,
): number => {
  const aSubscribed = Boolean(a.subscribed);
  const bSubscribed = Boolean(b.subscribed);
  if (aSubscribed !== bSubscribed) return aSubscribed ? -1 : 1;
  const aTime = (a.details.start_time ?? "").trim();
  const bTime = (b.details.start_time ?? "").trim();
  if (!aTime && !bTime) return 0;
  if (!aTime) return -1;
  if (!bTime) return 1;
  return aTime < bTime ? -1 : aTime > bTime ? 1 : 0;
};

/**
 * Student calendar — the RN `CalendarScreen`: a month grid with a dot on every
 * day that has events, and the selected day's events listed underneath. Month
 * navigation re-queries `getEventsMonthly`.
 */
const StudentCalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language = useSelector(
    (state: any) => state.setting?.language ?? "zh_hant",
  );
  const m = withMomentLocale(i18n.language);

  const todayKey = m().format("YYYY-MM-DD");
  const [viewYear, setViewYear] = useState(m().year());
  const [viewMonth, setViewMonth] = useState(m().month());
  const [selectedDate, setSelectedDate] = useState<string | null>(todayKey);
  const [events, setEvents] = useState<StudentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState<CalendarFilters>({ ...EMPTY_FILTERS });
  const [draftFilters, setDraftFilters] = useState<CalendarFilters>({
    ...EMPTY_FILTERS,
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const fetchMonth = useCallback(async (year: number, month: number) => {
    setLoading(true);
    const resp = await GetEventsMonthly(
      String(year),
      String(month + 1).padStart(2, "0"),
    );
    setLoading(false);

    const result = resp?.result;
    if (!Array.isArray(result)) {
      setEvents([]);
      setError(resp?.status !== -30014);
      return;
    }
    setEvents(result);
    setError(false);
  }, []);

  useEffect(() => {
    fetchMonth(viewYear, viewMonth);
  }, [fetchMonth, viewYear, viewMonth]);

  const handleChangeMonth = (year: number, month: number) => {
    setViewYear(year);
    setViewMonth(month);
    setSelectedDate(null);
  };

  const filteredEvents = useMemo(
    () =>
      events.filter((event) => {
        if (
          filters.postType !== ALL &&
          event.details.post_type !== filters.postType
        ) {
          return false;
        }
        if (
          filters.university !== ALL &&
          !toUniversityCodes(event.details.university).includes(
            filters.university,
          )
        ) {
          return false;
        }
        if (
          filters.society !== ALL &&
          !(event.organizers ?? []).some(
            (organizer) => organizer.id === filters.society,
          )
        ) {
          return false;
        }
        if (filters.subscribedOnly && !event.subscribed) return false;
        if (filters.multiDayOnly && !isMultiDayRun(event.details)) return false;
        return true;
      }),
    [events, filters],
  );

  const activeFilterCount = [
    filters.postType !== ALL,
    filters.university !== ALL,
    filters.society !== ALL,
    filters.subscribedOnly,
    filters.multiDayOnly,
  ].filter(Boolean).length;
  const hasActiveFilter = activeFilterCount > 0;

  const postTypes = useMemo(() => Object.keys(POST_TYPE_LABELS), []);

  const societyOptions = useMemo(() => {
    const map = new Map<string, string>();
    events.forEach((event) => {
      (event.organizers ?? []).forEach((organizer) => {
        if (!organizer.id || map.has(organizer.id)) return;
        map.set(
          organizer.id,
          pickLocalizedName(organizer, language) ||
            organizer.instagram_username ||
            organizer.name_en ||
            organizer.id,
        );
      });
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [events, language]);

  useEffect(() => {
    if (!filterModalOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFilterModalOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [filterModalOpen]);

  const openFilterModal = () => {
    setDraftFilters({ ...filters });
    setFilterModalOpen(true);
  };

  const applyFilters = () => {
    setFilters({ ...draftFilters });
    setFilterModalOpen(false);
  };

  const eventCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredEvents.forEach((event) => {
      const range = eventRangeKeys(event);
      if (!range) return;
      const start = parseEventDate(range[0]);
      if (!start) return;
      const cursor = start.clone();
      for (let step = 0; step < MAX_RANGE_DAYS; step += 1) {
        const key = cursor.format("YYYY-MM-DD");
        if (key > range[1]) break;
        counts[key] = (counts[key] ?? 0) + 1;
        cursor.add(1, "day");
      }
    });
    return counts;
  }, [filteredEvents]);

  const subscribedCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredEvents.forEach((event) => {
      if (!event.subscribed) return;
      const range = eventRangeKeys(event);
      if (!range) return;
      const start = parseEventDate(range[0]);
      if (!start) return;
      const cursor = start.clone();
      for (let step = 0; step < MAX_RANGE_DAYS; step += 1) {
        const key = cursor.format("YYYY-MM-DD");
        if (key > range[1]) break;
        counts[key] = (counts[key] ?? 0) + 1;
        cursor.add(1, "day");
      }
    });
    return counts;
  }, [filteredEvents]);

  const multiDayCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredEvents.forEach((event) => {
      if (!isMultiDayRun(event.details)) return;
      const range = eventRangeKeys(event);
      if (!range) return;
      const start = parseEventDate(range[0]);
      if (!start) return;
      const cursor = start.clone();
      for (let step = 0; step < MAX_RANGE_DAYS; step += 1) {
        const key = cursor.format("YYYY-MM-DD");
        if (key > range[1]) break;
        counts[key] = (counts[key] ?? 0) + 1;
        cursor.add(1, "day");
      }
    });
    return counts;
  }, [filteredEvents]);

  const multiDaySubscribedCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredEvents.forEach((event) => {
      if (!event.subscribed || !isMultiDayRun(event.details)) return;
      const range = eventRangeKeys(event);
      if (!range) return;
      const start = parseEventDate(range[0]);
      if (!start) return;
      const cursor = start.clone();
      for (let step = 0; step < MAX_RANGE_DAYS; step += 1) {
        const key = cursor.format("YYYY-MM-DD");
        if (key > range[1]) break;
        counts[key] = (counts[key] ?? 0) + 1;
        cursor.add(1, "day");
      }
    });
    return counts;
  }, [filteredEvents]);

  const selectedEvents = useMemo(() => {
    if (!selectedDate) return [];
    const seen = new Set<string>();
    return filteredEvents
      .filter((event) => {
        const range = eventRangeKeys(event);
        if (!range) return false;
        /**
         * The month grid puts an expanded occurrence (or an ordinary single-day
         * event) on its own start day and draws a continuous run as one band
         * across every day it covers, so the day panel follows the same rule:
         * a discrete event lists only on its start day, and only a true
         * multi-day run matches every day of its range. This is what keeps one
         * event from rendering more than once on a single day.
         */
        if (isMultiDayRun(event.details)) {
          return range[0] <= selectedDate && selectedDate <= range[1];
        }
        return range[0] === selectedDate;
      })
      .filter((event) => {
        const key = `${event.details.id}-${toDateKey(event.details.start_date)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort(compareBySubscribedThenTime);
  }, [filteredEvents, selectedDate]);

  const selectedLabel = selectedDate
    ? m(selectedDate, "YYYY-MM-DD").format("YYYY/MM/DD (ddd)")
    : "";

  const handleSubscribedChange = (eventId: string, subscribed: boolean) => {
    setEvents((prev) =>
      prev.map((item) =>
        item.details.id === eventId ? { ...item, subscribed } : item,
      ),
    );
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{t("Calendar")}</h1>
        <button
          type="button"
          onClick={openFilterModal}
          aria-haspopup="dialog"
          className={`inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-semibold transition ${
            hasActiveFilter
              ? "border-[#ffa217] bg-[#fff3e0] text-[#ffa217]"
              : "border-gray-200 bg-white text-gray-600 hover:border-[#ffa217] hover:text-[#ffa217]"
          }`}
        >
          <SlidersHorizontal size={15} />
          {t("Filter")}
          {hasActiveFilter && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#ffa217] px-1.5 text-[10px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
        <StudentMonthGrid
          year={viewYear}
          month={viewMonth}
          selectedDate={selectedDate}
          eventCounts={eventCounts}
          subscribedCounts={subscribedCounts}
          multiDayCounts={multiDayCounts}
          multiDaySubscribedCounts={multiDaySubscribedCounts}
          events={events}
          language={language}
          onSelectDate={setSelectedDate}
          onChangeMonth={handleChangeMonth}
        />

        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm lg:sticky lg:top-20 lg:flex lg:max-h-[calc(100vh-10rem)] lg:flex-col">
          <div className="mb-3 flex shrink-0 items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900">
                {selectedDate ? selectedLabel : t("Select a day")}
              </h2>
              {selectedDate && !loading && !error && (
                <p className="mt-0.5 text-xs text-gray-400">
                  {t("{{count}} events", { count: selectedEvents.length })}
                </p>
              )}
            </div>
            {loading && (
              <Loader2
                size={16}
                className="mt-0.5 shrink-0 animate-spin text-[#ffa217]"
              />
            )}
          </div>

          <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain lg:pr-1">
            {!selectedDate ? (
              <p className="py-8 text-center text-sm text-gray-400">
                {t("Tap a highlighted day to see its events.")}
              </p>
            ) : error ? (
              <AdminEmptyState
                iconType="MaterialCommunityIcons"
                iconName="cloud-alert-outline"
                title={t("Something went wrong")}
                message={t("Pull to refresh to load events.")}
                actionLabel={t("Retry")}
                onAction={() => fetchMonth(viewYear, viewMonth)}
              />
            ) : selectedEvents.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">
                {t("No events on this day")}
              </p>
            ) : (
              <div className="space-y-2.5">
                {selectedEvents.map((event) => {
                  const covers = toImageList(event.details.event_images);
                  const ended = hasEventEnded(
                    event.details.end_date,
                    event.details.end_time,
                  );
                  const run = isMultiDayRun(event.details);
                  const postTypeKey = postTypeLabelKey(event.details.post_type);
                  return (
                    <div
                      key={`${event.details.id}-${event.details.start_date ?? ""}`}
                      className={`group relative overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:shadow-md ${
                        ended
                          ? "border-gray-200"
                          : "border-gray-100 hover:border-[#ffd79a]"
                      }`}
                    >
                      <span
                        className={`absolute inset-y-0 left-0 w-1 ${
                          ended ? "bg-gray-300" : "bg-[#ffa217]"
                        }`}
                      />
                      <div className="flex gap-3 p-4 pl-5">
                        {covers.length > 0 && (
                          <img
                            src={covers[0]}
                            alt=""
                            loading="lazy"
                            className={`h-[72px] w-[72px] shrink-0 rounded-xl object-cover ring-1 ring-gray-100 ${
                              ended ? "opacity-80" : ""
                            }`}
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                            {postTypeKey && (
                              <span
                                className={`${POST_TYPE_BADGE_CLASS} shrink-0`}
                              >
                                {t(postTypeKey)}
                              </span>
                            )}
                            {run && (
                              <span className="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                {t("Multi-day")}
                              </span>
                            )}
                            {event.subscribed && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#fff3e0] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#ffa217]">
                                <BookmarkCheck size={10} />
                                {t("Subscribed")}
                              </span>
                            )}
                            {ended && (
                              <span className="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                {t("Ended")}
                              </span>
                            )}
                          </div>
                          <Link
                            to={`/student/events/${event.details.id}`}
                            state={{ event }}
                            title={
                              localized(
                                event.details.title,
                                event.details.title_en,
                                language,
                              ) || t("Untitled Event")
                            }
                            className="mt-2 block break-words text-[16px] font-bold leading-[1.4] text-gray-900 transition group-hover:text-[#ffa217]"
                          >
                            {localized(
                              event.details.title,
                              event.details.title_en,
                              language,
                            ) || t("Untitled Event")}
                          </Link>
                          <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-2.5">
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => downloadEventIcs(event, language)}
                                title={t("Add to calendar")}
                                aria-label={t("Add to calendar")}
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 transition hover:border-[#ffa217] hover:bg-[#fff8f0] hover:text-[#ffa217]"
                              >
                                <CalendarPlus size={16} />
                              </button>
                              <StudentSubscribeButton
                                eventId={event.details.id}
                                subscribed={Boolean(event.subscribed)}
                                onChange={(next) =>
                                  handleSubscribedChange(event.details.id, next)
                                }
                              />
                            </div>
                            <Link
                              to={`/student/events/${event.details.id}`}
                              state={{ event }}
                              className="inline-flex items-center gap-0.5 text-xs font-semibold text-[#ffa217] transition hover:text-[#e08f0e]"
                            >
                              {t("View details")}
                              <ChevronRight size={13} />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {filterModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setFilterModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("Filters")}
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">
                {t("Filters")}
              </h2>
              <button
                type="button"
                onClick={() => setFilterModalOpen(false)}
                aria-label={t("Close")}
                className="rounded-lg p-1 text-gray-400 transition hover:bg-gray-50 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>

            <section className="mb-5">
              <span className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-gray-400">
                {t("Filter by type")}
              </span>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: ALL, label: t("All") },
                  ...postTypes.map((value) => {
                    const labelKey = postTypeLabelKey(value);
                    return {
                      value,
                      label: labelKey ? t(labelKey) : value,
                    };
                  }),
                ].map((option) => {
                  const active = draftFilters.postType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          postType: option.value,
                        }))
                      }
                      aria-pressed={active}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        active
                          ? "border-[#ffa217] bg-[#ffa217] text-white"
                          : "border-gray-200 bg-white text-gray-600 hover:border-[#ffa217] hover:text-[#ffa217]"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="mb-5">
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  {t("Filter by university")}
                </span>
                <span className="relative block">
                  <select
                    value={draftFilters.university}
                    onChange={(event) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        university: event.target.value,
                      }))
                    }
                    className={FILTER_SELECT_CLASS}
                    aria-label={t("Filter by university")}
                  >
                    <option value={ALL}>{t("All")}</option>
                    {universityList.map((university) => (
                      <option key={university.value} value={university.value}>
                        {t(university.value)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </span>
              </label>
            </section>

            <section className="mb-5">
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  {t("Filter by society")}
                </span>
                <span className="relative block">
                  <select
                    value={draftFilters.society}
                    onChange={(event) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        society: event.target.value,
                      }))
                    }
                    className={FILTER_SELECT_CLASS}
                    aria-label={t("Filter by society")}
                  >
                    <option value={ALL}>{t("All")}</option>
                    {societyOptions.map((society) => (
                      <option key={society.id} value={society.id}>
                        {society.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </span>
              </label>
            </section>

            <section className="mb-5 space-y-2">
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700 transition hover:border-[#ffa217]/60">
                <span>{t("Subscribed only")}</span>
                <input
                  type="checkbox"
                  checked={draftFilters.subscribedOnly}
                  onChange={(event) =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      subscribedOnly: event.target.checked,
                    }))
                  }
                  className="h-4 w-4 accent-[#ffa217]"
                />
              </label>
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700 transition hover:border-[#ffa217]/60">
                <span>{t("Multi-day only")}</span>
                <input
                  type="checkbox"
                  checked={draftFilters.multiDayOnly}
                  onChange={(event) =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      multiDayOnly: event.target.checked,
                    }))
                  }
                  className="h-4 w-4 accent-[#ffa217]"
                />
              </label>
            </section>

            <div className="sticky bottom-0 -mx-5 -mb-5 flex items-center justify-between gap-3 border-t border-gray-100 bg-white/95 px-5 py-3 backdrop-blur">
              <button
                type="button"
                onClick={() => setDraftFilters({ ...EMPTY_FILTERS })}
                className="text-sm font-semibold text-[#ffa217] transition hover:text-[#f59200]"
              >
                {t("Clear all")}
              </button>
              <button
                type="button"
                onClick={applyFilters}
                className="rounded-xl bg-[#ffa217] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#f59200]"
              >
                {t("Apply filters")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCalendarPage;
